import React from 'react';

const join = (...parts) => parts.filter(Boolean).join(' ');

const SURFACE_VARIANT_CLASS = {
  default: 'data-surface--default',
  dense: 'data-surface--dense',
  comfortable: 'data-surface--comfortable',
  cardRows: 'data-surface--cardRows'
};

const TABLE_DENSITY_CLASS = {
  dense: {
    header: 'data-table__head-cell data-table__head-cell--dense',
    cell: 'data-table__cell data-table__cell--dense',
    row: 'data-table__row data-table__row--dense'
  },
  comfortable: {
    header: 'data-table__head-cell data-table__head-cell--comfortable',
    cell: 'data-table__cell data-table__cell--comfortable',
    row: 'data-table__row data-table__row--comfortable'
  },
  cardRows: {
    header: 'data-table__head-cell data-table__head-cell--cardRows',
    cell: 'data-table__cell data-table__cell--cardRows',
    row: 'data-table__row data-table__row--cardRows'
  }
};

const getDensity = (density) => TABLE_DENSITY_CLASS[density] || TABLE_DENSITY_CLASS.dense;

export function DataSurface({ as: Component = 'section', variant = 'default', className = '', children, ...props }) {
  const variantClass = SURFACE_VARIANT_CLASS[variant] || SURFACE_VARIANT_CLASS.default;
  return (
    <Component className={join('data-surface', variantClass, className)} {...props}>
      {children}
    </Component>
  );
}

export function DataTable({ className = '', density = 'dense', children, ...props }) {
  return (
    <table className={join('data-table w-full table-fixed', `data-table--${density}`, className)} {...props}>
      {children}
    </table>
  );
}

export function DataTableHeadCell({
  className = '',
  density = 'dense',
  align = 'left',
  children,
  ...props
}) {
  const densityClasses = getDensity(density);
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={join(densityClasses.header, alignClass, className)} {...props}>
      {children}
    </th>
  );
}

export function DataTableRow({
  as: Component = 'tr',
  className = '',
  density = 'dense',
  interactive = false,
  children,
  ...props
}) {
  const densityClasses = getDensity(density);
  return (
    <Component
      className={join(
        densityClasses.row,
        interactive ? 'data-table__row--interactive' : '',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

export function DataTableCell({
  as: Component = 'td',
  className = '',
  density = 'dense',
  align = 'left',
  children,
  ...props
}) {
  const densityClasses = getDensity(density);
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <Component className={join(densityClasses.cell, alignClass, className)} {...props}>
      {children}
    </Component>
  );
}

export function AmountCell({ value, className = '', density = 'dense', currencyPrefix = '$', ...props }) {
  return (
    <DataTableCell density={density} align="right" className={join('tabular-nums', className)} {...props}>
      <span className="data-table__amount">
        {currencyPrefix}
        {value}
      </span>
    </DataTableCell>
  );
}

export function StatusChip({ children, className = '' }) {
  return <span className={join('data-surface-status-chip', className)}>{children}</span>;
}

export function RowActionCell({ className = '', children, density = 'dense', ...props }) {
  return (
    <DataTableCell density={density} className={join('data-table__row-action', className)} {...props}>
      {children}
    </DataTableCell>
  );
}

export function InlineEditCell({ className = '', children, density = 'dense', ...props }) {
  return (
    <DataTableCell density={density} className={join('data-table__inline-edit', className)} {...props}>
      {children}
    </DataTableCell>
  );
}

export function EmptyStateRow({
  colSpan,
  children,
  density = 'dense',
  className = '',
  ...props
}) {
  return (
    <DataTableRow density={density} className={join('data-table__empty-row', className)} {...props}>
      <DataTableCell density={density} colSpan={colSpan} className="data-table__empty-cell">
        {children}
      </DataTableCell>
    </DataTableRow>
  );
}
